import { getFreshToken } from '@/lib/firebase';

/**
 * Thin authenticated REST client for the staff-api documents/ module
 * (claude-dev document-center 02-spec §5). Same pattern as trust-api.ts:
 * REST + StaffFirebaseGuard, Firebase bearer attached, base = the GraphQL URL
 * with /graphql stripped.
 */
const API_BASE = (import.meta.env.VITE_GRAPHQL_URL as string).replace(/\/graphql\/?$/, '');

/**
 * Mirrored from the api's `documents.types.ts` (vendored by copy — this repo shares no libs
 * with the backend). Since migration 003 the `doc_type` column has NO CHECK, so this list and
 * the api's are the only two places a type is named: keep them in step.
 *
 * `non_profit` sits next to `tax_exempt` rather than replacing it. A reseller's exemption
 * certificate and a non-profit's determination letter are both "tax exempt" loosely, and only
 * the second justifies the NON-PROFIT-10 coupon — so the operator picks which at filing time.
 * Both count as exemption evidence for that coupon (see `EXEMPTION_DOC_TYPES` server-side).
 *
 * `tax_withholding` sits next to them and is NOT a third exemption type: a W-9, W-8BEN/-E or a
 * foreign withholding certificate says what a payer must withhold from what it pays US, not what
 * we may stop charging the customer. It is absent from `EXEMPTION_DOC_TYPES` on both sides, so
 * filing one never satisfies the Apply Coupon gate and the dialog shows it no State field.
 *
 * THE ORDER IS THE UI ORDER — Type dropdown and type filter chips both render it as written
 * (requested 2026-08-27). `tax_withholding` follows `tax_exempt` so the two tax documents an
 * operator confuses sit side by side rather than one of them hiding at the end of the list.
 * `nda` sits directly under `contract` (requested 2026-10-01) — both commercial paper.
 * `ownership_transfer` (2026-10-06) holds the transfer form + owner letter behind an ownership
 * change; it sits just before `other` and is not an exemption type.
 * `other` stays last: it is the fallback, not a choice.
 */
export const DOC_TYPES = [
  'contract',
  'nda',
  'po',
  'non_profit',
  'tax_exempt',
  'tax_withholding',
  'ownership_transfer',
  'other',
] as const;
export type DocType = (typeof DOC_TYPES)[number];

export const DOC_TYPE_LABEL: Record<DocType, string> = {
  contract: 'Contract',
  nda: 'NDA',
  tax_exempt: 'Tax Exempt',
  tax_withholding: 'Tax Withholding',
  non_profit: 'Non-Profit',
  ownership_transfer: 'Ownership Transfer',
  po: 'PO',
  other: 'Other',
};

/**
 * The types that can justify a tax-exemption discount — the server's gate accepts either.
 * `tax_withholding` is deliberately not one of them: it is a tax document about what a payer
 * withholds, not evidence that this customer should not be charged tax.
 */
export const EXEMPTION_DOC_TYPES = ['tax_exempt', 'non_profit'] as const;
export const isExemptionDocType = (t: string): boolean =>
  (EXEMPTION_DOC_TYPES as readonly string[]).includes(t);

/**
 * There is deliberately NO tag vocabulary on this side any more.
 *
 * `non-profit` started life as a tag with a chip, a filter and a row badge; it is now a document
 * TYPE (`DOC_TYPES` above), which is what the Type column and the type chips render. The tag
 * still exists server-side — Apply Coupon stamps it on a `tax_exempt` certificate it accepted,
 * which is the case the type cannot cover — but nothing on the web reads it by name: it is found
 * through the search box, which matches tags server-side via `tags_flat` → `refs` → FTS5.
 *
 * So the tags a document carries travel in `CustomerDocument.tags` and are searched, not
 * labelled. Re-adding a badge or a chip means re-adding the vocabulary here on purpose.
 */

export type DocStatus =
  | 'valid'
  | 'expiring_soon'
  | 'expired'
  | 'pending_future'
  | 'invalid'
  | 'archived';

export interface CustomerDocument {
  id: string;
  accountId: string;
  docType: DocType;
  title: string;
  status: DocStatus;
  currentVersionId: string | null;
  currentFilename: string | null;
  counterpartyName: string | null;
  externalNumber: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  amountCents: number | null;
  currency: string | null;
  jurisdiction: string | null;
  isValid: boolean;
  verifiedBy: string | null;
  verifiedAt: string | null;
  notes: string | null;
  tags: string[];
  meta: Record<string, unknown>;
  source: string;
  sourceRef: string | null;
  legalHold: boolean;
  legalHoldBy: string | null;
  legalHoldReason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  archivedBy: string | null;
  versionCount?: number;
}

export interface DocumentVersion {
  id: string;
  documentId: string;
  versionNo: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  avStatus: string;
  uploadedBy: string | null;
  uploadedAt: string;
}

/**
 * What the server did to the customer's STRIPE record as a side effect of filing or re-typing
 * an exemption document. Present on `upload` and `patch` responses ONLY when the document is
 * `tax_exempt` / `non_profit` — every other document leaves this undefined.
 *
 * `taxStatusChanged` is the only field worth telling an operator about: it means THIS action
 * moved the customer from Taxable to Exempt, so future invoices stop being taxed. `taxStatus`
 * is what Stripe holds now (`undefined` = the account has no Stripe customer, or the read
 * failed — never assume `none`).
 *
 * Nothing here ever clears an exemption; see the API's `noteTaxExemptDocumentOnStripe`.
 */
export interface DocumentStripeNote {
  noted: boolean;
  taxStatus?: 'none' | 'exempt' | 'reverse';
  taxStatusChanged: boolean;
}

export interface DocumentPage {
  items: CustomerDocument[];
  total: number;
  /** Set when the full-text branch found nothing and trigram supplied these rows. */
  fuzzy?: boolean;
}

/** Fields accepted on upload. Sent as multipart text parts. */
export interface UploadFields {
  /** Either accountId or stripeId — the server resolves a Stripe id to the account. */
  accountId?: string;
  stripeId?: string;
  docType: DocType;
  title?: string;
  counterpartyName?: string;
  externalNumber?: string;
  effectiveDate?: string;
  expirationDate?: string;
  jurisdiction?: string;
  notes?: string;
  tags?: string[];
}

async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { token } = await getFreshToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        // NOTE: no Content-Type for FormData bodies — the browser must set the
        // multipart boundary itself. JSON callers pass it via init.headers.
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    // fetch rejects (TypeError: Failed to fetch) when the response never arrives OR when
    // it arrived without CORS headers — e.g. an nginx error page, which carries none. Say
    // that, rather than leaking the browser's contentless "Failed to fetch" into a toast.
    throw new Error(
      'Could not reach the staff API — the request was rejected before it arrived (network or API gateway).',
    );
  }
  if (!res.ok) {
    let msg = `Request failed (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message) msg = Array.isArray(body.message) ? body.message.join('; ') : body.message;
    } catch {
      // Not JSON — this response did not come from Nest. The gateway answered instead, and
      // the only one we can name usefully is its body-size cap. Branch on the PARSE, not on
      // the status: Nest also answers 413 (multer's 'File too large'), and that one is a
      // real oversized file, not an infra fault.
      if (res.status === 413) {
        msg =
          'The API gateway rejected this upload before it reached the server (HTTP 413 — request too large). The app allows 25 MB; the gateway is capped lower.';
      }
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

const json = { 'Content-Type': 'application/json' };

export const documentsApi = {
  list: (params: {
    accountId: string;
    q?: string;
    docType?: DocType;
    tags?: string[];
    includeArchived?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams({ accountId: params.accountId });
    if (params.q) qs.set('q', params.q);
    if (params.docType) qs.set('docType', params.docType);
    if (params.tags?.length) qs.set('tags', params.tags.join(','));
    if (params.includeArchived) qs.set('includeArchived', 'true');
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    return authedFetch<DocumentPage>(`/documents?${qs.toString()}`);
  },

  get: (id: string) =>
    authedFetch<{ document: CustomerDocument; versions: DocumentVersion[] }>(
      `/documents/${id}`,
    ),

  upload: (fields: UploadFields, file: File) => {
    const form = new FormData();
    if (fields.accountId) form.append('accountId', fields.accountId);
    if (fields.stripeId) form.append('stripeId', fields.stripeId);
    form.append('docType', fields.docType);
    if (fields.title) form.append('title', fields.title);
    if (fields.counterpartyName) form.append('counterpartyName', fields.counterpartyName);
    if (fields.externalNumber) form.append('externalNumber', fields.externalNumber);
    if (fields.effectiveDate) form.append('effectiveDate', fields.effectiveDate);
    if (fields.expirationDate) form.append('expirationDate', fields.expirationDate);
    if (fields.jurisdiction) form.append('jurisdiction', fields.jurisdiction);
    if (fields.notes) form.append('notes', fields.notes);
    // tags and meta travel as JSON text — the server parses them explicitly,
    // because class-transformer will not decode a JSON string in a multipart part.
    if (fields.tags?.length) form.append('tags', JSON.stringify(fields.tags));
    form.append('file', file);
    return authedFetch<{
      document: CustomerDocument;
      version: DocumentVersion;
      stripe?: DocumentStripeNote;
    }>('/documents', { method: 'POST', body: form });
  },

  addVersion: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return authedFetch<{ document: CustomerDocument; version: DocumentVersion }>(
      `/documents/${id}/versions`,
      { method: 'POST', body: form },
    );
  },

  /**
   * Mint a short-lived presigned URL. POST, not GET, so the ambient audit rail sees it
   * (audit.interceptor only audits POST/PUT/PATCH/DELETE) — do not "simplify" this to a
   * GET. The returned URL is a bearer token: never log it, never put it in a query string
   * we control, never persist it.
   */
  download: (id: string, disposition: 'attachment' | 'inline' = 'attachment') =>
    authedFetch<{ url: string; filename: string; expiresInSeconds: number }>(
      `/documents/${id}/download`,
      { method: 'POST', headers: json, body: JSON.stringify({ disposition }) },
    ),

  patch: (id: string, fields: Partial<CustomerDocument>) =>
    authedFetch<{ document: CustomerDocument; stripe?: DocumentStripeNote }>(`/documents/${id}`, {
      method: 'PATCH',
      headers: json,
      body: JSON.stringify(fields),
    }),

  verify: (id: string, verified: boolean) =>
    authedFetch<{ document: CustomerDocument }>(`/documents/${id}/verify`, {
      method: 'POST',
      headers: json,
      body: JSON.stringify({ verified }),
    }),

  archive: (id: string, reason?: string) =>
    authedFetch<{ document: CustomerDocument }>(`/documents/${id}/archive`, {
      method: 'POST',
      headers: json,
      body: JSON.stringify({ reason }),
    }),

  restore: (id: string) =>
    authedFetch<{ document: CustomerDocument }>(`/documents/${id}/restore`, {
      method: 'POST',
      headers: json,
    }),

  /**
   * PERMANENT deletion — R2 bytes (every version) plus the D1 metadata. ROOT only. The server
   * refuses unless the document is already archived, refuses under legal hold, and refuses
   * while the document is the tax-exemption evidence for a live discount. The access-ledger
   * row survives the document.
   */
  purge: (id: string) =>
    authedFetch<{ purged: { versionsDeleted: number; objectsDeleted: number } }>(
      `/documents/${id}`,
      { method: 'DELETE' },
    ),

  legalHold: (id: string, hold: boolean, reason?: string) =>
    authedFetch<{ document: CustomerDocument }>(`/documents/${id}/legal-hold`, {
      method: 'POST',
      headers: json,
      body: JSON.stringify({ hold, reason }),
    }),

  /**
   * Document Center: resolve a customer identifier and return what we hold for them.
   * The server detects the identifier kind and reports it back as `resolvedAs`.
   */
  customerSearch: (q: string) =>
    authedFetch<{
      resolvedAs: 'stripeId' | 'accountId' | 'email' | 'domain';
      term: string;
      scanned: number;
      accountsWithDocuments: number;
      truncated: boolean;
      matches: {
        accountId: string;
        companyName: string | null;
        /**
         * Identity facts for the account header, batched server-side from the same `companies`
         * read. Subscription status is NOT here — that is a Stripe call, fetched per shown
         * account by the card through the shared cache-first hook.
         */
        ownerEmail: string | null;
        plan: string | null;
        planFeature: string | null;
        isTrial: boolean | null;
        isActive: boolean | null;
        stripeId: string | null;
        pairedScreens: number | null;
        maxScreens: number | null;
        documents: CustomerDocument[];
      }[];
    }>(`/documents/customer-search?q=${encodeURIComponent(q)}`),

  /**
   * The Document Center's landing list: the biggest accounts we hold POs or contracts for,
   * ranked by LICENSED screens. Same match shape as `customerSearch`, so the page renders both
   * with one component.
   */
  topAccounts: () =>
    authedFetch<{
      scanned: number;
      matches: {
        accountId: string;
        companyName: string | null;
        ownerEmail: string | null;
        plan: string | null;
        planFeature: string | null;
        isTrial: boolean | null;
        isActive: boolean | null;
        stripeId: string | null;
        pairedScreens: number | null;
        maxScreens: number | null;
        /** The most recent PO or contract for this account. */
        document: CustomerDocument | null;
      }[];
    }>(`/documents/top-accounts`),

  expiring: (days: 30 | 60 | 90 = 60) =>
    authedFetch<{ items: CustomerDocument[]; total: number }>(
      `/documents/expiring?days=${days}`,
    ),
};
